import React, { FC, useState } from 'react'
import { KTSVG } from '../../../../../_metronic/helpers'
import type { QHSEDefectDto } from '../../../QHSE/core/_models'

interface Props {
  visible: boolean
  onClose: () => void
  onConfirm: (reason: string) => void
  request: QHSEDefectDto | null
}

const LABEL = 'form-label fw-semibold fs-6 mb-2 text-dark'
const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 1050,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

export const RejectJobRequestModal: FC<Props> = ({ visible, onClose, onConfirm, request }) => {
  const [reason, setReason] = useState('')
  const [errors, setErrors] = useState<{ reason?: string }>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  if (!visible || !request) return null

  const jobGroups = (() => {
    const groups = new Map<string, string[]>()
    const ids = request.jobPlanLineIds && request.jobPlanLineIds.length > 0
      ? request.jobPlanLineIds
      : request.jobPlanLineId
      ? [request.jobPlanLineId]
      : []
    const codes = request.jobPlanLineJobCodes || []
    const tasks = request.jobPlanLineTaskDescriptions || []
    const subNames = request.jobPlanLineSubComponentNames || []

    const push = (group: string, label: string) => {
      if (!label || !label.trim()) return
      const list = groups.get(group) ?? []
      if (!list.includes(label)) {
        list.push(label)
        groups.set(group, list)
      }
    }

    if (ids.length > 0) {
      ids.forEach((id, idx) => {
        const code = codes[idx]
        const task = tasks[idx]
        const label = code && task ? `${code} - ${task}` : code || task || (id ? `Plan #${id}` : '')
        const groupName = subNames[idx] || request.subComponentName || 'Unassigned Subcomponent'
        push(groupName, label)
      })
    } else if (request.jobPlanLineJobCode || request.jobPlanLineTaskDescription || request.jobPlanLineId) {
      const label = request.jobPlanLineJobCode && request.jobPlanLineTaskDescription
        ? `${request.jobPlanLineJobCode} - ${request.jobPlanLineTaskDescription}`
        : request.jobPlanLineJobCode || request.jobPlanLineTaskDescription || (request.jobPlanLineId ? `Plan #${request.jobPlanLineId}` : '')
      const groupName = request.subComponentName || 'Unassigned Subcomponent'
      push(groupName, label)
    }

    return Array.from(groups.entries()).map(([name, jobs]) => ({ name, jobs }))
  })()

  const subcomponentBadgeLabels =
    jobGroups.length > 0
      ? jobGroups.map((g) => g.name).filter((name) => name && name !== 'Unassigned Subcomponent')
      : request.subComponentNames && request.subComponentNames.length > 0
      ? request.subComponentNames.filter(Boolean)
      : request.subComponentName
      ? [request.subComponentName]
      : []

  const handleSubmit = async () => {
    const newErrors: { reason?: string } = {}
    if (!reason.trim()) {
      newErrors.reason = 'Rejection reason is required'
    }
    setErrors(newErrors)
    if (Object.keys(newErrors).length > 0) return

    setIsSubmitting(true)
    try {
      await onConfirm(reason.trim())
      setReason('')
      setErrors({})
    } catch (error) {
      // Error handling is done in parent
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setReason('')
    setErrors({})
    onClose()
  }

  return (
    <div style={WRAP_STYLE} tabIndex={-1}>
      <div
        className='bg-white text-dark rounded shadow-lg d-flex flex-column'
        style={{
          width: '600px',
          maxWidth: '90vw',
          maxHeight: '90vh',
        }}
      >
        <div className='d-flex align-items-center justify-content-between border-bottom px-4 py-3 flex-shrink-0'>
          <h5 className='modal-title text-dark m-0'>Reject Job Request</h5>
          <button type='button' className='btn-close' onClick={handleClose} />
        </div>

        <div className='flex-grow-1 overflow-auto px-4 py-3'>
          <div className='alert alert-warning d-flex align-items-start mb-4'>
            <KTSVG path='/media/icons/duotune/general/gen044.svg' className='svg-icon-2 me-2 mt-1' />
            <div>
              <strong>Rejecting this job request will:</strong>
              <ul className='mb-0 mt-2'>
                <li>Mark the job request as "Rejected"</li>
                <li>No job will be created from this defect</li>
                <li>The defect will remain unchanged</li>
              </ul>
            </div>
          </div>

          <div className='mb-4'>
            <div className='row g-3'>
              <div className='col-12'>
                <label className={LABEL}>Defect Number</label>
                <div className='bg-light rounded p-3'>
                  <span className='badge badge-light-primary'>{request.defectNumber || `D-${request.id}`}</span>
                </div>
              </div>

              <div className='col-12'>
                <label className={LABEL}>Description</label>
                <div className='bg-light rounded p-3' style={{ minHeight: '80px' }}>
                  {request.description || <span className='text-muted'>No description</span>}
                </div>
              </div>

              <div className='col-md-6'>
                <label className={LABEL}>Vessel</label>
                <div className='bg-light rounded p-3 h-100'>{request.vesselName || 'N/A'}</div>
              </div>

              <div className='col-md-6'>
                <label className={LABEL}>Equipment</label>
                <div className='bg-light rounded p-3 h-100'>
                  {request.equipmentName ? (
                    <span className='badge badge-light-primary text-dark border border-primary'>
                      {request.equipmentName}
                    </span>
                  ) : (
                    <span className='text-muted'>N/A</span>
                  )}
                </div>
              </div>

              <div className='col-md-6'>
                <label className={LABEL}>Subcomponents</label>
                <div className='bg-light rounded p-3 h-100'>
                  {subcomponentBadgeLabels.length > 0 ? (
                    <div className='d-flex flex-wrap gap-2'>
                      {subcomponentBadgeLabels.map((name, idx) => (
                        <span key={`${name}-${idx}`} className='badge badge-light-info text-dark border border-info'>
                          {name}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className='text-muted'>N/A</span>
                  )}
                </div>
              </div>

              <div className='col-md-6'>
                <label className={LABEL}>Job Plan (Grouped by Subcomponent)</label>
                <div className='bg-light rounded p-3 h-100'>
                  {jobGroups.length > 0 ? (
                    <div className='d-flex flex-column gap-3'>
                      {jobGroups.map((group) => (
                        <div key={group.name} className='bg-white rounded p-2 border border-dashed border-gray-200'>
                          <div className='mb-2'>
                            <span className='badge badge-light-info text-dark border border-info'>{group.name}</span>
                          </div>
                          <div className='d-flex flex-wrap gap-2'>
                            {group.jobs.map((label, idx) => (
                              <span key={`${group.name}-${idx}`} className='badge badge-light-success text-dark border border-success'>
                                {label}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className='text-muted'>N/A</span>
                  )}
                </div>
              </div>

              <div className='col-12'>
                <label className={LABEL}>
                  Rejection Reason <span className='text-danger'>*</span>
                </label>
                <textarea
                  className={`form-control text-dark ${errors.reason ? 'is-invalid' : ''}`}
                  rows={4}
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value)
                    if (errors.reason) {
                      setErrors(prev => ({ ...prev, reason: undefined }))
                    }
                  }}
                  placeholder='Please provide a reason for rejecting this job request...'
                  required
                />
                {errors.reason && (
                  <div className='invalid-feedback'>{errors.reason}</div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className='d-flex justify-content-end gap-2 border-top px-4 py-3 flex-shrink-0'>
          <button
            type='button'
            className='btn btn-light'
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type='button'
            className='btn btn-danger'
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className='spinner-border spinner-border-sm me-2'></span>
                Rejecting...
              </>
            ) : (
              <>
                <KTSVG path='/media/icons/duotune/general/gen027.svg' className='svg-icon-3 me-2' />
                Reject Request
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
